import { Fragment } from 'react';
import { AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai';
import clsx from 'clsx';
import { IBlogContentItem } from 'common/types/blog';
import { IAnyType } from 'common/types/common';
import { normalizeLocalizedHref, normalizeLocalizedHtml } from 'common/utils/url';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { useHook } from './hook';

export interface IFaqItemType {
  title: string;
  content?: IBlogContentItem[];
  value?: string;
}

interface Props {
  className?: string;
  content: IFaqItemType[];
}

export const BlogFAQ: React.FC<Props> = ({ className, content }) => {
  const { i18n } = useTranslation();
  const normalizeHtml = (value: string) => normalizeLocalizedHtml(value, i18n.language);
  const { activeElements, handleClick } = useHook({ content });

  const renderContent = (item: IAnyType, index: number) => {
    if (item.type === 'paragraph') {
      if (Array.isArray(item.value)) {
        return (
          <p key={`content-${index}`} className={clsx('mb-6 mt-2 max-w-full', item.className)}>
            {item.value.map((innerItem: IAnyType, innerItemIndex: number) => {
              if (innerItem.type === 'h3') {
                return (
                  <h3
                    key={`content-${innerItemIndex}`}
                    className={clsx('heading-3 mb-1 font-semibold', innerItem.className)}
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                  />
                );
              }
              if (innerItem.type === 'link') {
                const normalizedHref = normalizeLocalizedHref(innerItem.href, i18n.language);
                return (
                  <Link
                    key={`content-${innerItemIndex}`}
                    aria-label={innerItem.value}
                    className="text-primary"
                    href={normalizedHref}
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                  />
                );
              }
              if (innerItem.type === 'text') {
                return (
                  <span
                    key={`content-${innerItemIndex}`}
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                  />
                );
              }
              return <></>;
            })}
          </p>
        );
      }
      return (
        <p
          key={`content-${index}`}
          className={clsx('mb-6 mt-2 max-w-full')}
          dangerouslySetInnerHTML={{ __html: normalizeHtml(item.value) }}></p>
      );
    }
    if (item.type === 'ul') {
      return (
        <ul key={`content-${index}`} className={clsx('list-disc pl-4', item.className)}>
          {item.content.map((li: IAnyType, listIndex: number) => {
            if (Array.isArray(li.value)) {
              return (
                <li key={`list-${listIndex}`} className="mt-3 max-w-full">
                  {li.value.map((innerItem: IAnyType, innerItemIndex: number) => {
                    if (innerItem.type === 'h3') {
                      return (
                        <div
                          key={`content-${innerItemIndex}`}
                          className={clsx('heading-3 mb-1 font-semibold', innerItem.className)}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    if (innerItem.type === 'link') {
                      const normalizedHref = normalizeLocalizedHref(innerItem.href, i18n.language);
                      return (
                        <Link
                          key={`content-${innerItemIndex}`}
                          aria-label={innerItem.value}
                          className="text-primary"
                          href={normalizedHref}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    if (innerItem.type === 'text') {
                      return (
                        <span
                          key={`content-${innerItemIndex}`}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    return <></>;
                  })}
                </li>
              );
            }
            return (
              <li
                key={`list-${listIndex}`}
                className="mt-3 max-w-full"
                dangerouslySetInnerHTML={{ __html: normalizeHtml(li.value) }}
              />
            );
          })}
        </ul>
      );
    }
    if (item.type === 'ol') {
      return (
        <ul key={`content-${index}`} className={clsx('list-decimal pl-4', item.className)}>
          {item.content.map((li: IAnyType, listIndex: number) => {
            if (Array.isArray(li.value)) {
              return (
                <li key={`list-${listIndex}`} className="mt-3 max-w-full">
                  {li.value.map((innerItem: IAnyType, innerItemIndex: number) => {
                    if (innerItem.type === 'h3') {
                      return (
                        <div
                          key={`content-${innerItemIndex}`}
                          className={clsx('heading-3 mb-1 font-semibold', innerItem.className)}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    if (innerItem.type === 'link') {
                      const normalizedHref = normalizeLocalizedHref(innerItem.href, i18n.language);
                      return (
                        <Link
                          key={`content-${innerItemIndex}`}
                          aria-label={innerItem.value}
                          className="text-primary"
                          href={normalizedHref}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    if (innerItem.type === 'text') {
                      return (
                        <span
                          key={`list-${listIndex}`}
                          dangerouslySetInnerHTML={{ __html: normalizeHtml(innerItem.value) }}
                        />
                      );
                    }
                    return <></>;
                  })}
                </li>
              );
            }
            return (
              <li
                key={`list-${listIndex}`}
                className="mt-3 max-w-full"
                dangerouslySetInnerHTML={{ __html: normalizeHtml(li.value) }}
              />
            );
          })}
        </ul>
      );
    }
    return <></>;
  };

  return (
    <div id="accordion-collapse" className={clsx('pb-3', className)} data-accordion="open">
      {content.map((item: IFaqItemType, index: number) => {
        const key = `accordion-collapse-heading-${index + 1}`;
        const active = activeElements.includes(key);
        return (
          <Fragment key={index.toString()}>
            <button
              id={`accordion-collapse-heading-${index + 1}`}
              type="button"
              className={clsx(
                'flex w-full items-center justify-between border-b-[1px] border-b-[#382848] px-5 py-4',
                {
                  'bg-[#261B31]': active,
                  'bg-[#261B31AA]': !active,
                },
              )}
              data-accordion-target={`#accordion-collapse-body-${index + 1}`}
              aria-expanded={active}
              aria-controls={`accordion-collapse-body-${index + 1}`}
              onClick={handleClick(key)}>
              <h3
                className="mb-1 pr-2 text-left text-[18px] font-bold leading-6 text-white md:text-[20px] lg:text-[22px] lg:leading-8"
                dangerouslySetInnerHTML={{ __html: normalizeHtml(item.title) }}
              />

              {active ? (
                <AiOutlineMinus className="text-white" />
              ) : (
                <AiOutlinePlus className="text-white" />
              )}
            </button>

            {!!item.value && (
              <div
                id={`accordion-collapse-body-${index + 1}`}
                className="hidden bg-[#261B3144] p-5 pt-3 text-white"
                aria-labelledby={`accordion-collapse-body-${index + 1}`}
                dangerouslySetInnerHTML={{ __html: normalizeHtml(item.value) }}
              />
            )}
            {!!item.content && (
              <div
                id={`accordion-collapse-body-${index + 1}`}
                className="hidden bg-[#261B3144] p-5 pt-3 text-white"
                aria-labelledby={`accordion-collapse-body-${index + 1}`}>
                {item.content.map(renderContent)}
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
